import { useTranslation } from '@rhino-project/core/i18n';
import { use } from 'react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectIndicator,
  SelectPopover,
  ListBox,
  ListBoxItem,
  useOverlayState
} from '@heroui/react';
import { Icon } from '@iconify/react';
import {
  useBaseOwnerId,
  useFeature,
  useHasRoleOf,
  useModelIndex
} from '@rhino-project/core/hooks';
import { useRouter } from '@tanstack/react-router';
import TeamContext from '../../contexts/TeamContext';
import OtsSidebarForm from '../ots/OtsSidebarForm';

const ADD_TEAM_KEY = '__add_team__';

type TeamResource = {
  id: string;
  name: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
};

export const TeamSwitcher = () => {
  const { t } = useTranslation();

  const { team, setContext } = use(TeamContext);
  const baseOwnerId = useBaseOwnerId();
  const isAdmin = useHasRoleOf('admin');
  const addTeamEnabled = useFeature('multiple_teams');
  const canAddTeam = isAdmin && addTeamEnabled;
  const router = useRouter();
  const addTeamState = useOverlayState();

  const { resources: teams } = useModelIndex('team', {
    filter: { organization_id: baseOwnerId },
    order: ['created_at'],
    queryOptions: { enabled: !!baseOwnerId }
  });

  const results = teams?.results as TeamResource[] | undefined;
  const hasMultipleTeams = (teams?.total ?? 0) > 1;

  if (!team || !results || (!hasMultipleTeams && !canAddTeam)) return null;

  return (
    <>
      <Select
        aria-label={t('frontend.messages.switch_team')}
        variant="secondary"
        fullWidth
        value={String(team.id)}
        onChange={(key) => {
          if (key === ADD_TEAM_KEY) {
            addTeamState.open();
            return;
          }
          const next = results.find((t) => String(t.id) === String(key));
          if (!next) return;
          setContext(next);
          router.navigate({
            to: '/$owner',
            params: { owner: String(baseOwnerId) }
          });
        }}
      >
        <SelectTrigger className="items-center gap-2.5 px-2.5 py-2 rounded-field bg-surface-secondary border border-border w-full">
          <Icon className="size-4 shrink-0" icon="bi:people" />
          <SelectValue />
          <SelectIndicator />
        </SelectTrigger>
        <SelectPopover>
          <ListBox>
            {[
              ...results.map((t) => (
                <ListBoxItem key={t.id} id={String(t.id)} textValue={t.name}>
                  {t.name}
                </ListBoxItem>
              )),
              ...(canAddTeam
                ? [
                    <ListBoxItem
                      key={ADD_TEAM_KEY}
                      id={ADD_TEAM_KEY}
                      textValue={t('frontend.messages.add_team')}
                    >
                      <span className="flex items-center gap-2 text-accent">
                        <Icon icon="bi:plus-circle" className="size-4" />
                        {t('frontend.messages.add_team')}
                      </span>
                    </ListBoxItem>
                  ]
                : [])
            ]}
          </ListBox>
        </SelectPopover>
      </Select>
      <OtsSidebarForm
        isOpen={addTeamState.isOpen}
        onModalClose={addTeamState.close}
        onSave={(created: TeamResource) => {
          setContext(created);
          router.navigate({
            to: '/$owner',
            params: { owner: String(baseOwnerId) }
          });
        }}
      />
    </>
  );
};
